import { FC, useState } from "react";
import {
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from "@altimateai/lego";
import { Artifact } from "../../types";
import { CodeBlock, Stack } from "@altimateai/lego";
import {
  DownloadIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "../../../../assets/icons";
import JSZip from "jszip";

interface ArtifactsProps {
  artifacts: Artifact[];
}

export const Artifacts: FC<ArtifactsProps> = ({ artifacts }) => {
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(
    null,
  );
  const [showAll, setShowAll] = useState(false);

  if (!artifacts?.length) return null;

  const visibleArtifacts = showAll ? artifacts : artifacts.slice(0, 3);
  const hasMore = artifacts.length > 3;

  const handleDownloadAll = async () => {
    const zip = new JSZip();

    // Add each artifact as a file to the zip
    artifacts.forEach((artifact) => {
      const fileName = `${artifact.name}.${artifact.type}`;
      zip.file(fileName, artifact.content);
    });

    try {
      // Generate the zip file
      const content = await zip.generateAsync({ type: "blob" });

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = "artifacts.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error creating zip file:", error);
    }
  };

  return (
    <div className="al-mt-4" role="region" aria-label="Artifacts">
      <Stack direction="row" justify="start" align="center" className="al-mb-2">
        <div className="al-text-sm al-font-medium">Artifacts:</div>
        <Button
          variant="link"
          size="sm"
          onClick={handleDownloadAll}
          className="al-gap-2"
        >
          <DownloadIcon className="al-h-4 al-w-4" />
          Download All
        </Button>
      </Stack>
      <Stack direction="row" wrap align="center">
        {visibleArtifacts.map((artifact) => (
          <button
            key={artifact.name}
            onClick={() => setSelectedArtifact(artifact)}
            className="al-p-0 al-m-0 al-border-0 al-bg-transparent"
            aria-label={`View ${artifact.name} content`}
          >
            <Badge
              variant="outline"
              className="hover:al-bg-gray-100 al-cursor-pointer"
            >
              {artifact.name}
            </Badge>
          </button>
        ))}
        {hasMore && (
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="al-gap-1 al-text-gray-500"
            aria-expanded={showAll}
            aria-label={showAll ? "Show less artifacts" : "Show more artifacts"}
          >
            {showAll ? (
              <>
                Show Less <ChevronUpIcon className="al-h-4 al-w-4" />
              </>
            ) : (
              <>
                Show More ({artifacts.length - 3}){" "}
                <ChevronDownIcon className="al-h-4 al-w-4" />
              </>
            )}
          </Button>
        )}
      </Stack>

      <Dialog
        open={!!selectedArtifact}
        onOpenChange={() => setSelectedArtifact(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedArtifact?.name}</DialogTitle>
          </DialogHeader>
          <div className="al-mt-4">
            <CodeBlock
              code={selectedArtifact?.content || ""}
              // @ts-expect-error valid type
              language={selectedArtifact?.type || "sql"}
              editorTheme="tomorrow"
              theme="light"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
